import { cloneElement, createElement } from 'preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';

const DEBUG_SCROLL_INSET = 0;

export type VirtualListProps<Entry> = {
  data: Entry[],
  renderRow: (e: Entry, idx: number, isSelected: boolean) => createElement.JSX.Element,
  rowHeight: number,
  selected: number | null,
};

export function VirtualList<T>(props: VirtualListProps<T>) {
  const { data, renderRow, rowHeight, selected } = props;

  const [ scrollOffset, setScrollOffset ] = useState(DEBUG_SCROLL_INSET);
  const [ selectionBeforeScroll, setSelectionBeforeScroll ] = useState(selected);
  const [ viewportHeight, setViewportHeight ] = useState(1000);
  const outerRef = useRef<HTMLDivElement>(null);

  function onScroll(evt: Event) {
    setScrollOffset(Math.floor((evt.target as HTMLElement)?.scrollTop + DEBUG_SCROLL_INSET));
  }

  useLayoutEffect(() => {
    if (!outerRef.current) return;
    const realHeight = outerRef.current.getBoundingClientRect().height;
    setViewportHeight(realHeight - 2 * DEBUG_SCROLL_INSET);
  });

  function calculateOffset(index: number): number {
    return rowHeight * index;
  }

  function getRowHeight(index: number): number {
    // for now only fixed row height is supported
    return rowHeight;
  }

  useEffect(() => {
    const outer = outerRef.current;
    if (!outer || selected === null) return;
    const selectedTop = calculateOffset(selected);
    if (selectedTop < outer.scrollTop) {
      outer.scroll({
        top: selectedTop,
      });
    }
    const outerHeight = outer.getBoundingClientRect().height;
    if (selectedTop + getRowHeight(selected) > outer.scrollTop + outerHeight) {
      outer.scroll({
        top: selectedTop - outerHeight + getRowHeight(selected),
      });
    }
  }, [data, selected, selectionBeforeScroll]);

  const firstItem = Math.max(0, Math.floor(scrollOffset / rowHeight));
  const numberOfItemsInView = Math.ceil(viewportHeight / rowHeight) + 1;
  const rowsInView = data.slice(firstItem, firstItem + numberOfItemsInView);

  function realRenderRow(entry: T, renderIndex: number) {
    const index = renderIndex + firstItem;
    return renderRow(entry, index, selected == index);
  }

  return (<>
  <div onScroll={onScroll} style={
      {
        overflowY: 'scroll',
        overflowX: 'hidden',
      }
    }
    ref={outerRef}
  >
    <ul style={{
      margin: 0,
      padding: 0,
      paddingTop: calculateOffset(firstItem),
      height: data.length * rowHeight,
      position: 'relative',
    }}>
      {rowsInView.map(realRenderRow)}
    </ul>
  </div>
</>);
}
